import { Link } from '@tanstack/react-router';
import { AvatarDisc, Button, LoadingState, MonoLabel, PageHeader, Row, SectionHeader, Star } from '@/component';
import { useBlockedList, useFavourites, useFriendActions, useFriendFeed, useFriendList } from '@/hook';
import { formatDayLabel } from '@/lib/time';
import { FriendSearch } from './FriendSearch';
import { RelationAction } from './RelationAction';
import { personName } from './personName';

const FEED_ROWS = 20;
const plural = (n: number, noun: string) => `${n} ${noun}${n === 1 ? '' : 's'}`;

/** /friends: what friends sang on the left; requests to answer, the friend
 * list with how much each has sung, and the people you blocked on the right. */
export function FriendsPage() {
  const list = useFriendList();
  const feed = useFriendFeed(FEED_ROWS);
  const blocked = useBlockedList();
  const { favourites, toggle } = useFavourites();
  const { accept, decline } = useFriendActions();

  return (
    <>
      <PageHeader
        eyebrow={list ? `${plural(list.friends.length, 'friend')} · ${plural(list.requests.length, 'request')}` : 'Friends'}
        title="Friends"
        action={<FriendSearch size="lg" />}
      />
      {!list || !feed ? (
        <LoadingState />
      ) : (
        <div className="mt-12 grid grid-cols-1 items-start gap-14 lg:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)]">
          <section>
            <SectionHeader title="Recently sung" />
            {feed.length === 0 && (
              <p className="py-6 text-sm text-mute">
                {list.friends.length === 0
                  ? 'Add a friend by their @handle and what they sing shows up here.'
                  : 'Your friends have not sung yet. It shows here when they do.'}
              </p>
            )}
            {feed.map((item) => (
              <Row key={item.historyId} className="items-start gap-4 py-[19px]">
                {item.singer.handle ? (
                  <Link to="/u/$handle" params={{ handle: item.singer.handle }} aria-label={`${personName(item.singer)}'s profile`}>
                    <AvatarDisc user={item.singer} size={38} />
                  </Link>
                ) : (
                  <AvatarDisc user={item.singer} size={38} />
                )}
                <div className="min-w-0 flex-1 text-[15px] leading-[1.45]">
                  <span className="font-semibold">{personName(item.singer)}</span> sang{' '}
                  <Link to="/tracks/$trackId" params={{ trackId: item.trackId }} className="font-semibold hover:text-acc">
                    {item.title}
                  </Link>
                  {item.artist && <> · {item.artist}</>}
                  <MonoLabel className="mt-1.5 block tracking-[0.1em]">
                    {[formatDayLabel(item.sangAt), item.venue, item.systemName].filter(Boolean).join(' · ')}
                  </MonoLabel>
                </div>
                <Star on={favourites.has(item.trackId)} onToggle={() => toggle(item.trackId)} />
              </Row>
            ))}
          </section>

          <div className="flex flex-col gap-10">
            {list.requests.length > 0 && (
              <section>
                <h2 className="border-b border-line pb-3 text-[15px] font-semibold tracking-[-0.01em] text-acc">Requests</h2>
                {list.requests.map((req) => (
                  <Row key={req.requestId} className="gap-3">
                    <AvatarDisc user={req} size={32} />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-semibold">{personName(req)}</div>
                      {req.handle && <MonoLabel className="block normal-case tracking-normal text-[11px]">@{req.handle}</MonoLabel>}
                    </div>
                    <Button size="sm" className="px-3 py-[7px] text-xs" onClick={() => void accept(req.requestId)}>
                      Accept
                    </Button>
                    <Button variant="text" size="sm" quiet className="text-xs" onClick={() => void decline(req.requestId)}>
                      Decline
                    </Button>
                  </Row>
                ))}
              </section>
            )}
            <section>
              <h2 className="border-b border-line pb-3 text-[15px] font-semibold tracking-[-0.01em]">Your friends</h2>
              {list.friends.length === 0 && <p className="py-6 text-sm text-mute">No friends yet.</p>}
              {list.friends.map((friend) =>
                friend.handle ? (
                  <Link key={friend.userId} to="/u/$handle" params={{ handle: friend.handle }} className="flex items-center gap-3 border-b border-stripeA py-[13px] hover:text-acc">
                    <AvatarDisc user={friend} size={30} />
                    <span className="min-w-0 flex-1 truncate text-sm font-semibold">{personName(friend)}</span>
                    <span className="shrink-0 font-mono text-[11px] tabular-nums text-faint">{friend.songs}</span>
                  </Link>
                ) : (
                  <Row key={friend.userId} className="gap-3 py-[13px]">
                    <AvatarDisc user={friend} size={30} />
                    <span className="min-w-0 flex-1 truncate text-sm font-semibold">{personName(friend)}</span>
                    <span className="shrink-0 font-mono text-[11px] tabular-nums text-faint">{friend.songs}</span>
                  </Row>
                )
              )}
            </section>
            {blocked && blocked.length > 0 && (
              <section>
                <h2 className="border-b border-line pb-3 text-[15px] font-semibold tracking-[-0.01em] text-mute">Blocked</h2>
                {blocked.map((person) => (
                  <Row key={person.userId} className="gap-3 py-[13px]">
                    <AvatarDisc user={person} size={30} />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-semibold">{personName(person)}</div>
                      {person.handle && <MonoLabel className="block normal-case tracking-normal text-[11px]">@{person.handle}</MonoLabel>}
                    </div>
                    <RelationAction userId={person.userId} relation={{ state: 'blocked' }} />
                  </Row>
                ))}
              </section>
            )}
          </div>
        </div>
      )}
    </>
  );
}
